// OTel browser SDK initialisation — traces, metrics, and logs.
//
// Wires native OpenTelemetry alongside the existing Sentry stack:
//   - Traces: WebTracerProvider + BatchSpanProcessor → OTLPTraceExporter.
//     Auto-instrumentations (document-load, fetch, XHR, user-interaction)
//     supplied by @opentelemetry/auto-instrumentations-web.
//   - Metrics: MeterProvider + PeriodicExportingMetricReader →
//     OTLPMetricExporter. Web Vitals (LCP, INP, CLS, FCP, TTFB) recorded
//     as histograms named `web_vitals.<name>`.
//   - Logs: LoggerProvider + BatchLogRecordProcessor → OTLPLogExporter.
//   - Every fetch / XHR to admin-web's own backends gets a W3C `traceparent`
//     header so backend services can join the same trace.
//   - Sentry is NOT touched here — the two stacks coexist.
//
// Call `initOtel()` from the bootstrap pipeline BEFORE the Vue app is created
// so the instrumentation hooks are in place before any fetch is made.

import { metrics, type Histogram } from '@opentelemetry/api'
import { logs } from '@opentelemetry/api-logs'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { W3CTraceContextPropagator } from '@opentelemetry/core'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { Resource } from '@opentelemetry/resources'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
// ATTR_DEPLOYMENT_ENVIRONMENT_NAME is in the incubating export (semver-stable but
// still under active review by the OTel spec — kept behind the /incubating path).
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME } from '@opentelemetry/semantic-conventions/incubating'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'

import { config } from '@core/config'

/**
 * URL patterns that will receive the `traceparent` / `tracestate` headers.
 * Limited to admin-web's own backends — never propagates to third-party domains.
 */
const PROPAGATE_TARGETS: RegExp[] = [
  // Auth service (VITE_AUTH_API_URL)
  new RegExp(`^${escapeRegExp(config.authApiUrl)}`),
  // Admin service (VITE_INVESTOR_API_URL — legacy name, actually admin-service)
  new RegExp(`^${escapeRegExp(config.adminApiUrl)}`),
]

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Web-vitals metric names we capture, in the order onX callbacks fire. */
const WEB_VITALS_NAMES = ['LCP', 'INP', 'CLS', 'FCP', 'TTFB'] as const

function wireWebVitals(): void {
  const meter = metrics.getMeter(config.app.name, config.app.version)

  // Pre-create one histogram per vital; record() is cheap.
  const histograms: Record<(typeof WEB_VITALS_NAMES)[number], Histogram> = {
    LCP: meter.createHistogram('web_vitals.lcp', {
      description: 'Largest Contentful Paint (ms)',
      unit: 'ms',
    }),
    INP: meter.createHistogram('web_vitals.inp', {
      description: 'Interaction to Next Paint (ms)',
      unit: 'ms',
    }),
    CLS: meter.createHistogram('web_vitals.cls', {
      description: 'Cumulative Layout Shift (unitless)',
      unit: '1',
    }),
    FCP: meter.createHistogram('web_vitals.fcp', {
      description: 'First Contentful Paint (ms)',
      unit: 'ms',
    }),
    TTFB: meter.createHistogram('web_vitals.ttfb', {
      description: 'Time To First Byte (ms)',
      unit: 'ms',
    }),
  }

  const record = (metric: Metric): void => {
    const histogram = histograms[metric.name as (typeof WEB_VITALS_NAMES)[number]]
    if (!histogram) return
    histogram.record(metric.value, {
      rating: metric.rating,
      navigation_type: metric.navigationType,
    })
  }

  onLCP(record)
  onINP(record)
  onCLS(record)
  onFCP(record)
  onTTFB(record)
}

/**
 * Initialise the OTel browser SDK (traces + metrics + logs). No-ops when
 * `VITE_OTEL_OTLP_ENDPOINT` is not set so local dev without a collector is
 * unaffected. When `VITE_OTEL_ENABLED=false` is set explicitly, this also
 * skips even if an endpoint is configured.
 */
export const initOtel = (): void => {
  if (!config.otel.enabled || !config.otel.otlpEndpoint) {
    // No collector configured or explicitly disabled — skip silently.
    return
  }

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: config.otel.serviceName,
    [ATTR_SERVICE_VERSION]: config.app.version,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: config.appEnv,
  })

  // ── Traces ────────────────────────────────────────────────────────────
  const traceExporter = new OTLPTraceExporter({
    url: config.otel.tracesUrl,
  })

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(traceExporter)],
  })

  tracerProvider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  })

  // ── Metrics ───────────────────────────────────────────────────────────
  const metricExporter = new OTLPMetricExporter({
    url: config.otel.metricsUrl,
  })

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: config.otel.metricExportIntervalMs,
      }),
    ],
  })

  metrics.setGlobalMeterProvider(meterProvider)

  // ── Logs ──────────────────────────────────────────────────────────────
  const logExporter = new OTLPLogExporter({
    url: config.otel.logsUrl,
  })

  const loggerProvider = new LoggerProvider({ resource })
  loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(logExporter))
  logs.setGlobalLoggerProvider(loggerProvider)

  // ── Auto-instrumentations (web) ──────────────────────────────────────
  // Covers document-load, fetch, XHR, and user-interaction in one call.
  // Per-instrumentation config opts fetch/XHR into trace-header propagation
  // for our own backends only — never third-party domains.
  registerInstrumentations({
    tracerProvider,
    meterProvider,
    instrumentations: getWebAutoInstrumentations({
      '@opentelemetry/instrumentation-fetch': {
        propagateTraceHeaderCorsUrls: PROPAGATE_TARGETS,
      },
      '@opentelemetry/instrumentation-xml-http-request': {
        propagateTraceHeaderCorsUrls: PROPAGATE_TARGETS,
      },
    }),
  })

  // ── Web Vitals (LCP / INP / CLS / FCP / TTFB) ────────────────────────
  // Not covered by auto-instrumentations-web; emitted as OTLP histograms
  // named `web_vitals.<name>` on the meter created above.
  wireWebVitals()
}
